import type { PayloadAction, ThunkAction } from '@reduxjs/toolkit'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import type { Duration } from '~/components/time/util'
import { useUserPrefs } from '~/hooks/useUserPrefs'

import {
  applyAddRow,
  applyChangeRowLayout,
  applyMoveOrSwap,
  applyRemoveRow,
  applyRemoveWidget,
  applySetWidget,
  applySetWidgetSettings
} from '~/components/customize/layoutHelpers'
import type { DashboardLayout, RowLayout, SlotSize, WidgetInstance } from '~/components/customize/types'
import type { AppDispatch, RootState } from '~/store'
import { useCurrentOrgId } from '~/store/slices/currentOrg'
import { dispatcherFactory, selectorHookFactory } from '~/store/utils'

import type { TimeZone } from '~/data/types/timezone'

import { CHARGER_ACTIVITY_REPORT_TEMPLATE_ID, normalizeReportLayout } from './v2/customize/reportWidgetConfig'

/** Matches the legacy Report Builder / transactions-api filter shape. */
export type ReportCommissioningStateFilter = 'COMMISSION' | 'DECOMMISSION' | 'PRECOMMISSION'

/**
 * ⚠️  CRITICAL: PERSISTED SLICE - READ BEFORE MAKING ANY CHANGES ⚠️
 *
 * This Redux slice is PERSISTED TO LocalStorage. Existing users have this state
 * stored in their browsers and it will be automatically loaded on page load.
 *
 * 🚨 BREAKING CHANGE RISKS:
 * - Adding non-optional fields to this slice will BREAK existing users when their
 *   persisted state is loaded from LocalStorage
 * - Renaming existing fields will cause runtime errors for existing users
 * - Changing field types may cause type mismatches and crashes
 *
 * 📋 SAFE CHANGE PRACTICES:
 * ✅ Adding optional fields with default values (configuring a default in `initialState` DOES NOT WORK)
 * ✅ Adding new optional properties to existing objects
 * ✅ Adding new reducer actions (these don't affect persisted state structure)
 * ✅ Modifying selector logic that handles missing fields gracefully
 *
 * 🚫 UNSAFE PRACTICES:
 * ❌ Adding required fields without migration strategy
 * ❌ Renaming or removing existing fields
 * ❌ Changing field types (string -> number, etc.)
 * ❌ Restructuring nested objects without backwards compatibility
 *
 * 💡 IF YOU MUST MAKE BREAKING CHANGES:
 * 1. Implement a migration strategy in the persistence layer
 * 2. Consider versioning the persisted state schema
 * 3. Test thoroughly with existing persisted state scenarios
 * 4. Plan a rollout strategy that handles users with old state
 *
 * Remember: This affects REAL USERS in production who may have months/years
 * of persisted state. One careless change can break the app for thousands of users.
 */

/**
 * Legacy global filter shape. Retained for persisted-state compatibility (the
 * field still exists in users' LocalStorage). Read once per legacy page to seed
 * that page's `reportFilters` bucket (see `_setActiveLegacyReportPage`), after
 * which all reads and writes go through `reportFilters`.
 */
type UserReportsState = {
  filters: {
    energy: {
      orgId?: string
      siteIds: string[]
    }
    vehicles: {
      orgId?: string
    }
  }
}

/**
 * Per-user customizable report layouts, mirroring the dashboard's `dashboards`
 * map. Optional and lazy-seeded: users who have never customized a report have
 * no entry here and the customize hooks fall back to the registered template.
 */
type UserReportLayoutsState = {
  activeLayoutId: string
  layouts: Record<string, DashboardLayout>
}

/** ISO-serialized custom date range for Redux persistence. */
type StoredCustomTimeRange = {
  from: string
  to: string
}

/**
 * Page-level filters scoped to a single saved report within the current org
 * context (mirrors the dashboard's per-org filter buckets). Each saved report
 * remembers its own selections so switching reports restores the filters the
 * user last used for that report.
 */
type ReportFilterState = {
  orgId?: string
  siteIds: string[]
  /** Report timezone; falls back to the user's preferred timezone when unset. */
  timeZone?: TimeZone
  nodeIds: string[]
  commissioningStates: ReportCommissioningStateFilter[]
  fleetId?: string
  duration?: Duration
  customTimeRange?: StoredCustomTimeRange
}

type ReportFiltersByReportId = Record<string, ReportFilterState>
type ReportFiltersByOrg = Record<string, ReportFiltersByReportId>
type UserReportFiltersEntry = ReportFiltersByOrg | ReportFiltersByReportId

type ReportsSlice = {
  currentUser?: string
  userStates: {
    [key: string]: UserReportsState
  }
  reportLayouts?: {
    [userId: string]: UserReportLayoutsState
  }
  /**
   * Per-user filter selections keyed by org context, then report id. Optional
   * and lazy-seeded: a report with no entry falls back to "current org, all sites".
   */
  reportFilters?: {
    [userId: string]: UserReportFiltersEntry
  }
  /**
   * Per-user display order for saved reports in the library. Optional and
   * lazy-seeded: reports missing from the list are appended at the end.
   */
  savedReportOrder?: {
    [userId: string]: string[]
  }
}

const initialUserState: UserReportsState = {
  filters: {
    energy: {
      orgId: undefined,
      siteIds: []
    },
    vehicles: {
      orgId: undefined
    }
  }
}

const initialState: ReportsSlice = {
  userStates: {}
}

// Stable empty fallback so selectors return a referentially-stable value when a
// report has no saved filters yet (avoids spurious re-renders).
const EMPTY_SITE_IDS: string[] = []
const EMPTY_NODE_IDS: string[] = []
const EMPTY_COMMISSIONING_STATES: ReportCommissioningStateFilter[] = []
const EMPTY_REPORT_FILTERS: ReportFilterState = {
  siteIds: EMPTY_SITE_IDS,
  nodeIds: EMPTY_NODE_IDS,
  commissioningStates: EMPTY_COMMISSIONING_STATES
}

/**
 * Synthetic filter-bucket ids for the legacy (non-customizable) report pages.
 * They never have a layout entry — they exist purely so `/reports/energy` and
 * `/reports/vehicles` each get their own filter bucket instead of reading and
 * writing whichever saved report happened to be active last.
 */
export type LegacyReportPage = 'energy' | 'vehicles'
const LEGACY_REPORT_PAGE_IDS: Record<LegacyReportPage, string> = {
  energy: 'legacy-energy-report-page',
  vehicles: 'legacy-vehicle-report-page'
}

// The id of the report whose filters are currently in effect. Falls back to the
// default template id so a fresh session has a single, stable bucket.
function getActiveReportId(state: ReportsSlice): string {
  if (!state.currentUser) return CHARGER_ACTIVITY_REPORT_TEMPLATE_ID
  return state.reportLayouts?.[state.currentUser]?.activeLayoutId ?? CHARGER_ACTIVITY_REPORT_TEMPLATE_ID
}

function isOldUserReportFiltersFormat(entry: UserReportFiltersEntry): entry is ReportFiltersByReportId {
  const firstKey = Object.keys(entry)[0]
  if (!firstKey) return false
  const firstValue = entry[firstKey]
  return firstValue !== null && typeof firstValue === 'object' && 'siteIds' in firstValue
}

function normalizeReportFilterState(saved?: ReportFilterState): ReportFilterState {
  if (!saved) return EMPTY_REPORT_FILTERS
  return {
    orgId: saved.orgId,
    siteIds: saved.siteIds ?? EMPTY_SITE_IDS,
    timeZone: saved.timeZone,
    nodeIds: saved.nodeIds ?? EMPTY_NODE_IDS,
    commissioningStates: saved.commissioningStates ?? EMPTY_COMMISSIONING_STATES,
    fleetId: saved.fleetId,
    duration: saved.duration,
    customTimeRange: saved.customTimeRange
  }
}

function getOrgReportFiltersReadonly(
  state: ReportsSlice,
  userId: string,
  orgId: string,
  principalOrgId?: string
): ReportFiltersByReportId {
  const entry = state.reportFilters?.[userId]
  if (!entry) return {}

  if (isOldUserReportFiltersFormat(entry)) {
    const migrationOrgId = principalOrgId || orgId
    if (orgId === migrationOrgId) return entry
    return {}
  }

  return entry[orgId] ?? {}
}

function getOrCreateOrgReportFilters(
  state: ReportsSlice,
  userId: string,
  orgId: string,
  principalOrgId?: string
): ReportFiltersByReportId {
  if (!state.reportFilters) state.reportFilters = {}
  let entry = state.reportFilters[userId]

  if (!entry) {
    entry = {}
    state.reportFilters[userId] = entry
  }

  if (isOldUserReportFiltersFormat(entry)) {
    const migrationOrgId = principalOrgId || orgId
    const migrated: ReportFiltersByOrg = {
      [migrationOrgId]: { ...entry }
    }
    state.reportFilters[userId] = migrated
    entry = migrated
  }

  const byOrg = entry as ReportFiltersByOrg
  if (!byOrg[orgId]) {
    byOrg[orgId] = {}
  }

  return byOrg[orgId]!
}

// Selector-safe: never mutates the store, returns a stable empty fallback.
function getActiveReportFiltersReadonly(
  state: ReportsSlice,
  orgId: string,
  principalOrgId?: string
): ReportFilterState {
  if (!state.currentUser) return EMPTY_REPORT_FILTERS
  const reportId = getActiveReportId(state)
  const saved = getOrgReportFiltersReadonly(state, state.currentUser, orgId, principalOrgId)[reportId]
  return normalizeReportFilterState(saved)
}

// Reducer-only: ensures the active report's filter entry exists and returns a
// mutable reference to it. Must not be used in selectors.
function getActiveReportFilters(
  state: ReportsSlice,
  orgId: string,
  principalOrgId?: string
): ReportFilterState {
  if (!state.currentUser) {
    return { siteIds: [], nodeIds: [], commissioningStates: [] }
  }

  const reportId = getActiveReportId(state)
  const orgFilters = getOrCreateOrgReportFilters(state, state.currentUser, orgId, principalOrgId)
  let filters = orgFilters[reportId]
  if (!filters) {
    filters = { siteIds: [], nodeIds: [], commissioningStates: [] }
    orgFilters[reportId] = filters
  }
  if (!filters.nodeIds) filters.nodeIds = []
  if (!filters.commissioningStates) filters.commissioningStates = []
  return filters
}

type ReportFilterOrgContext = {
  filterOrgId: string
  principalOrgId?: string
}

function getReportFilterOrgContext(state: RootState): ReportFilterOrgContext {
  return {
    filterOrgId: state.currentOrganization.cloakedOrgs.at(-1)?.id ?? '',
    principalOrgId: state.currentOrganization.principalOrgId
  }
}

const reportsSlice = createSlice({
  name: 'reports',
  initialState,
  reducers: {
    // Clears the active report's filters (back to "current org, all sites").
    reset: (state, action: PayloadAction<ReportFilterOrgContext>) => {
      if (!state.currentUser) return
      const orgFilters = getOrCreateOrgReportFilters(
        state,
        state.currentUser,
        action.payload.filterOrgId,
        action.payload.principalOrgId
      )
      delete orgFilters[getActiveReportId(state)]
    },
    // Clears every saved report's filters for the user in the current org context.
    resetAll: (state, action: PayloadAction<ReportFilterOrgContext>) => {
      if (!state.currentUser || !state.reportFilters?.[state.currentUser]) return
      const entry = state.reportFilters[state.currentUser]!
      if (isOldUserReportFiltersFormat(entry)) {
        delete state.reportFilters[state.currentUser]
        return
      }
      delete (entry as ReportFiltersByOrg)[action.payload.filterOrgId]
    },
    setCurrentUser: (state, action: PayloadAction<string>) => {
      state.currentUser = action.payload
      // Retained for persisted-state compatibility; superseded by reportFilters.
      if (!state.userStates[action.payload]) state.userStates[action.payload] = { ...initialUserState }
    },
    setEnergyOrgId: (
      state,
      action: PayloadAction<{ orgId?: string } & ReportFilterOrgContext>
    ) => {
      const { orgId: nextOrgId, filterOrgId, principalOrgId } = action.payload
      const filters = getActiveReportFilters(state, filterOrgId, principalOrgId)
      const previousOrgId = filters.orgId
      filters.orgId = nextOrgId
      if (previousOrgId !== nextOrgId) {
        filters.siteIds = []
        filters.nodeIds = []
      }
    },
    setEnergySiteIds: (state, action: PayloadAction<{ siteIds: string[] } & ReportFilterOrgContext>) => {
      const { siteIds, filterOrgId, principalOrgId } = action.payload
      const filters = getActiveReportFilters(state, filterOrgId, principalOrgId)
      filters.siteIds = siteIds
      filters.nodeIds = []
    },
    setVehicleOrgId: (
      state,
      action: PayloadAction<{ orgId?: string } & ReportFilterOrgContext>
    ) => {
      const { orgId: nextOrgId, filterOrgId, principalOrgId } = action.payload
      const filters = getActiveReportFilters(state, filterOrgId, principalOrgId)
      const previousOrgId = filters.orgId
      filters.orgId = nextOrgId
      if (previousOrgId !== nextOrgId) {
        filters.fleetId = undefined
        // Vehicle/trip reports expose a site filter too — sites belong to the
        // previous org, so keep them from leaking into the new org's queries.
        filters.siteIds = []
        filters.nodeIds = []
      }
    },
    setReportTimeZone: (
      state,
      action: PayloadAction<{ timeZone?: TimeZone } & ReportFilterOrgContext>
    ) => {
      const { timeZone, filterOrgId, principalOrgId } = action.payload
      getActiveReportFilters(state, filterOrgId, principalOrgId).timeZone = timeZone
    },
    setReportNodeIds: (state, action: PayloadAction<{ nodeIds: string[] } & ReportFilterOrgContext>) => {
      const { nodeIds, filterOrgId, principalOrgId } = action.payload
      getActiveReportFilters(state, filterOrgId, principalOrgId).nodeIds = nodeIds
    },
    setReportCommissioningStates: (
      state,
      action: PayloadAction<{ commissioningStates: ReportCommissioningStateFilter[] } & ReportFilterOrgContext>
    ) => {
      const { commissioningStates, filterOrgId, principalOrgId } = action.payload
      getActiveReportFilters(state, filterOrgId, principalOrgId).commissioningStates = commissioningStates
    },
    setReportFleetId: (
      state,
      action: PayloadAction<{ fleetId?: string } & ReportFilterOrgContext>
    ) => {
      const { fleetId, filterOrgId, principalOrgId } = action.payload
      getActiveReportFilters(state, filterOrgId, principalOrgId).fleetId = fleetId
    },
    setReportDuration: (state, action: PayloadAction<{ duration: Duration } & ReportFilterOrgContext>) => {
      const { duration, filterOrgId, principalOrgId } = action.payload
      getActiveReportFilters(state, filterOrgId, principalOrgId).duration = duration
    },
    setReportCustomTimeRange: (
      state,
      action: PayloadAction<{ customTimeRange?: StoredCustomTimeRange } & ReportFilterOrgContext>
    ) => {
      const { customTimeRange, filterOrgId, principalOrgId } = action.payload
      getActiveReportFilters(state, filterOrgId, principalOrgId).customTimeRange = customTimeRange
    },
    _ensureReportFilterOrgState: (state, action: PayloadAction<ReportFilterOrgContext>) => {
      if (!state.currentUser) return
      getOrCreateOrgReportFilters(
        state,
        state.currentUser,
        action.payload.filterOrgId,
        action.payload.principalOrgId
      )
    },
    _seedReportLayout: (state, action: PayloadAction<{ userId: string; layout: DashboardLayout }>) => {
      const { userId, layout } = action.payload
      if (!state.reportLayouts) state.reportLayouts = {}
      let userLayouts = state.reportLayouts[userId]
      if (!userLayouts) {
        userLayouts = { activeLayoutId: layout.id, layouts: {} }
        state.reportLayouts[userId] = userLayouts
      }
      if (!userLayouts.layouts[layout.id]) {
        userLayouts.layouts[layout.id] = layout
      }
      userLayouts.activeLayoutId = layout.id
    },
    _moveOrSwapReportWidget: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; fromSlotId: string; toSlotId: string }>
    ) => {
      const { userId, layoutId, fromSlotId, toSlotId } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      state.reportLayouts![userId]!.layouts[layoutId] = applyMoveOrSwap(existing, fromSlotId, toSlotId)
    },
    _removeReportWidgetFromSlot: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; slotId: string }>
    ) => {
      const { userId, layoutId, slotId } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      state.reportLayouts![userId]!.layouts[layoutId] = applyRemoveWidget(existing, slotId)
    },
    _setReportWidgetInSlot: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; slotId: string; widget: WidgetInstance }>
    ) => {
      const { userId, layoutId, slotId, widget } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      state.reportLayouts![userId]!.layouts[layoutId] = applySetWidget(existing, slotId, widget)
    },
    /**
     * Re-templates the user's ACTIVE report in place: swaps its rows for the
     * given template's rows while preserving the report's id and name (used by
     * the "Reset" action). When the user has no saved reports yet, this creates
     * the first one from the template. Other saved reports are never touched.
     */
    _applyReportTemplate: (state, action: PayloadAction<{ userId: string; template: DashboardLayout }>) => {
      const { userId, template } = action.payload
      if (!state.reportLayouts) state.reportLayouts = {}
      const userLayouts = state.reportLayouts[userId]
      if (!userLayouts) {
        state.reportLayouts[userId] = {
          activeLayoutId: template.id,
          layouts: { [template.id]: template }
        }
        return
      }
      const activeId = userLayouts.activeLayoutId
      const existing = userLayouts.layouts[activeId]
      userLayouts.layouts[activeId] = {
        ...template,
        id: activeId,
        basedOn: template.id,
        name: existing?.name
      }
    },
    /**
     * Inserts a fully-formed report (a cloned template or duplicated layout with
     * its own id + name) into the user's collection and makes it active. Used by
     * "New report" and "Duplicate".
     */
    _addReport: (state, action: PayloadAction<{ userId: string; report: DashboardLayout }>) => {
      const { userId, report } = action.payload
      if (!state.reportLayouts) state.reportLayouts = {}
      let userLayouts = state.reportLayouts[userId]
      if (!userLayouts) {
        userLayouts = { activeLayoutId: report.id, layouts: {} }
        state.reportLayouts[userId] = userLayouts
      }
      userLayouts.layouts[report.id] = report
      userLayouts.activeLayoutId = report.id
      if (!state.savedReportOrder) state.savedReportOrder = {}
      const order = state.savedReportOrder[userId] ?? []
      if (!order.includes(report.id)) state.savedReportOrder[userId] = [...order, report.id]
    },
    // Copies a report's saved filters onto another report (used by "Duplicate"
    // so the copy inherits the source's org + site selection).
    _copyReportFilters: (
      state,
      action: PayloadAction<{ userId: string; fromReportId: string; toReportId: string } & ReportFilterOrgContext>
    ) => {
      const { userId, fromReportId, toReportId, filterOrgId, principalOrgId } = action.payload
      const source = getOrgReportFiltersReadonly(state, userId, filterOrgId, principalOrgId)[fromReportId]
      if (!source) return
      const orgFilters = getOrCreateOrgReportFilters(state, userId, filterOrgId, principalOrgId)
      orgFilters[toReportId] = {
        orgId: source.orgId,
        siteIds: [...source.siteIds],
        timeZone: source.timeZone,
        nodeIds: [...(source.nodeIds ?? [])],
        commissioningStates: [...(source.commissioningStates ?? [])],
        fleetId: source.fleetId,
        duration: source.duration,
        customTimeRange: source.customTimeRange
          ? { from: source.customTimeRange.from, to: source.customTimeRange.to }
          : undefined
      }
    },
    _renameReport: (state, action: PayloadAction<{ userId: string; reportId: string; name: string }>) => {
      const { userId, reportId, name } = action.payload
      const layout = state.reportLayouts?.[userId]?.layouts[reportId]
      if (!layout) return
      layout.name = name
    },
    _deleteReport: (state, action: PayloadAction<{ userId: string; reportId: string }>) => {
      const { userId, reportId } = action.payload
      const userLayouts = state.reportLayouts?.[userId]
      if (!userLayouts || !userLayouts.layouts[reportId]) return
      delete userLayouts.layouts[reportId]
      const order = state.savedReportOrder?.[userId]
      if (order) state.savedReportOrder![userId] = order.filter((id) => id !== reportId)
      const remaining = Object.keys(userLayouts.layouts)
      if (remaining.length === 0) {
        // No reports left — drop the entry so the surface falls back to the
        // default template (synthetic default report).
        delete state.reportLayouts![userId]
        return
      }
      if (userLayouts.activeLayoutId === reportId) {
        userLayouts.activeLayoutId = remaining[0]!
      }
    },
    _setActiveReportId: (state, action: PayloadAction<{ userId: string; reportId: string }>) => {
      const { userId, reportId } = action.payload
      const userLayouts = state.reportLayouts?.[userId]
      if (!userLayouts || !userLayouts.layouts[reportId]) return
      userLayouts.activeLayoutId = reportId
    },
    /**
     * Marks a pristine, built-in template (a "standard report") as active without
     * persisting a copy of it. The active id points at a template id that has no
     * entry in `layouts`, so the surface renders it read-only and the user keeps
     * receiving template updates. Editing requires duplicating it into a real
     * saved report first.
     */
    _setActiveStandardReport: (state, action: PayloadAction<{ userId: string; templateId: string }>) => {
      const { userId, templateId } = action.payload
      if (!state.reportLayouts) state.reportLayouts = {}
      const existing = state.reportLayouts[userId]
      if (existing) {
        existing.activeLayoutId = templateId
      } else {
        state.reportLayouts[userId] = { activeLayoutId: templateId, layouts: {} }
      }
    },
    /**
     * Scopes the filter hooks to one of the legacy report pages (see
     * `LEGACY_REPORT_PAGE_IDS`). Dispatched on legacy-page mount so those pages
     * never read or clobber a saved report's filters. On first use the page's
     * bucket is seeded from the legacy persisted global filter shape
     * (`userStates[userId].filters`), migrating pre-existing selections.
     */
    _setActiveLegacyReportPage: (
      state,
      action: PayloadAction<{ userId: string; page: LegacyReportPage } & ReportFilterOrgContext>
    ) => {
      const { userId, page, filterOrgId, principalOrgId } = action.payload
      const reportId = LEGACY_REPORT_PAGE_IDS[page]
      if (!state.reportLayouts) state.reportLayouts = {}
      const userLayouts = state.reportLayouts[userId]
      if (userLayouts) {
        userLayouts.activeLayoutId = reportId
      } else {
        state.reportLayouts[userId] = { activeLayoutId: reportId, layouts: {} }
      }

      const orgFilters = getOrCreateOrgReportFilters(state, userId, filterOrgId, principalOrgId)
      if (orgFilters[reportId]) return

      const legacyFilters = state.userStates[userId]?.filters
      orgFilters[reportId] = {
        orgId: page === 'energy' ? legacyFilters?.energy.orgId : legacyFilters?.vehicles.orgId,
        siteIds: page === 'energy' ? [...(legacyFilters?.energy.siteIds ?? [])] : [],
        nodeIds: [],
        commissioningStates: []
      }
    },
    _reorderSavedReports: (
      state,
      action: PayloadAction<{ userId: string; fromIndex: number; toIndex: number }>
    ) => {
      const { userId, fromIndex, toIndex } = action.payload
      const layouts = state.reportLayouts?.[userId]?.layouts
      if (!layouts) return
      if (!state.savedReportOrder) state.savedReportOrder = {}
      let order = state.savedReportOrder[userId]
      if (!order || order.length === 0) {
        order = Object.keys(layouts)
        state.savedReportOrder[userId] = order
      }
      if (fromIndex < 0 || fromIndex >= order.length || toIndex < 0 || toIndex >= order.length) return
      const next = [...order]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(toIndex, 0, moved!)
      state.savedReportOrder[userId] = next
    },
    _changeReportRowLayout: (
      state,
      action: PayloadAction<{
        userId: string
        layoutId: string
        rowId: string
        nextLayout: RowLayout
        newSlotIds: string[]
        minSizeByWidgetId: Record<string, SlotSize | undefined>
      }>
    ) => {
      const { userId, layoutId, rowId, nextLayout, newSlotIds, minSizeByWidgetId } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      const { layout } = applyChangeRowLayout(
        normalizeReportLayout(existing),
        rowId,
        nextLayout,
        newSlotIds,
        (id) => minSizeByWidgetId[id]
      )
      state.reportLayouts![userId]!.layouts[layoutId] = layout
    },
    _addReportRow: (
      state,
      action: PayloadAction<{
        userId: string
        layoutId: string
        afterRowId: string | null
        newRowLayout: RowLayout
        newRowId: string
        newSlotIds: string[]
      }>
    ) => {
      const { userId, layoutId, afterRowId, newRowLayout, newRowId, newSlotIds } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      state.reportLayouts![userId]!.layouts[layoutId] = applyAddRow(
        existing,
        afterRowId,
        newRowLayout,
        newRowId,
        newSlotIds
      )
    },
    _removeReportRow: (state, action: PayloadAction<{ userId: string; layoutId: string; rowId: string }>) => {
      const { userId, layoutId, rowId } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      state.reportLayouts![userId]!.layouts[layoutId] = applyRemoveRow(existing, rowId)
    },
    _setReportWidgetSettings: (
      state,
      action: PayloadAction<{ userId: string; layoutId: string; slotId: string; settings: unknown }>
    ) => {
      const { userId, layoutId, slotId, settings } = action.payload
      const existing = state.reportLayouts?.[userId]?.layouts[layoutId]
      if (!existing) return
      state.reportLayouts![userId]!.layouts[layoutId] = applySetWidgetSettings(existing, slotId, settings)
    }
  }
})

export default reportsSlice.reducer

const selectActiveReportFilters = createSelector(
  (state: RootState) => state.reports,
  (state: RootState) => state.currentOrganization.cloakedOrgs.at(-1)?.id ?? '',
  (state: RootState) => state.currentOrganization.principalOrgId,
  (reports, orgId, principalOrgId) => getActiveReportFiltersReadonly(reports, orgId, principalOrgId)
)

// Energy-specific hooks. Filters are scoped to the active saved report.
export const useSelectedReportsEnergyOrgId = selectorHookFactory((state) => selectActiveReportFilters(state).orgId)

export const useSelectedReportsEnergySiteIds = selectorHookFactory(
  (state) => selectActiveReportFilters(state).siteIds
)

// Vehicle-specific hooks. Filters are scoped to the active saved report.
export const useSelectedReportsVehicleOrgId = selectorHookFactory((state) => selectActiveReportFilters(state).orgId)

export const useSelectedReportsTimeZone = selectorHookFactory((state) => selectActiveReportFilters(state).timeZone)

export const useSelectedReportsDuration = selectorHookFactory(
  (state) => selectActiveReportFilters(state).duration ?? 'day'
)

export const useSelectedReportsStoredCustomTimeRange = selectorHookFactory(
  (state) => selectActiveReportFilters(state).customTimeRange
)

/** Id of the report whose filters are in effect (saved report, template, or legacy page bucket). */
export const useSelectedReportsActiveReportId = selectorHookFactory((state) =>
  getActiveReportId(state.reports)
)

export const useSelectedReportsNodeIds = selectorHookFactory((state) => {
  const nodeIds = selectActiveReportFilters(state).nodeIds
  return nodeIds.length > 0 ? nodeIds : EMPTY_NODE_IDS
})

export const useSelectedReportsCommissioningStates = selectorHookFactory((state) => {
  const states = selectActiveReportFilters(state).commissioningStates
  return states.length > 0 ? states : EMPTY_COMMISSIONING_STATES
})

export const useSelectedReportsFleetId = selectorHookFactory((state) => selectActiveReportFilters(state).fleetId)

const {
  reset,
  resetAll,
  setCurrentUser,
  setEnergyOrgId,
  setEnergySiteIds,
  setVehicleOrgId,
  setReportTimeZone,
  setReportNodeIds,
  setReportCommissioningStates,
  setReportFleetId,
  setReportDuration,
  setReportCustomTimeRange,
  _ensureReportFilterOrgState,
  _seedReportLayout,
  _moveOrSwapReportWidget,
  _removeReportWidgetFromSlot,
  _setReportWidgetInSlot,
  _applyReportTemplate,
  _addReport,
  _copyReportFilters,
  _renameReport,
  _deleteReport,
  _setActiveReportId,
  _setActiveStandardReport,
  _setActiveLegacyReportPage,
  _reorderSavedReports,
  _changeReportRowLayout,
  _addReportRow,
  _removeReportRow,
  _setReportWidgetSettings
} = reportsSlice.actions
export { reset, resetAll }

const updateReportsEnergyOrgId =
  (orgId?: string): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setEnergyOrgId({ orgId, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsEnergySiteIds =
  (siteIds: string[]): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setEnergySiteIds({ siteIds, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsVehicleOrgId =
  (orgId?: string): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setVehicleOrgId({ orgId, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsTimeZone =
  (timeZone?: TimeZone): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setReportTimeZone({ timeZone, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsNodeIds =
  (nodeIds: string[]): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setReportNodeIds({ nodeIds, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsCommissioningStates =
  (commissioningStates: ReportCommissioningStateFilter[]): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setReportCommissioningStates({ commissioningStates, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsFleetId =
  (fleetId?: string): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setReportFleetId({ fleetId, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsDuration =
  (duration: Duration): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setReportDuration({ duration, ...getReportFilterOrgContext(getState()) }))
  }

const updateReportsCustomTimeRange =
  (customTimeRange?: StoredCustomTimeRange): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> =>
  (dispatch, getState) => {
    dispatch(setReportCustomTimeRange({ customTimeRange, ...getReportFilterOrgContext(getState()) }))
  }

const ensureReportFilterOrgState =
  (): ThunkAction<void, RootState, unknown, PayloadAction<unknown>> => (dispatch, getState) => {
    dispatch(_ensureReportFilterOrgState(getReportFilterOrgContext(getState())))
  }

// Energy-specific dispatchers
export const useUpdateReportsEnergyOrgId = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((orgId?: string) => dispatch(updateReportsEnergyOrgId(orgId)), [dispatch])
}

export const useUpdateReportsEnergySiteIds = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((siteIds: string[]) => dispatch(updateReportsEnergySiteIds(siteIds)), [dispatch])
}

// Vehicle-specific dispatchers
export const useUpdateReportsVehicleOrgId = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((orgId?: string) => dispatch(updateReportsVehicleOrgId(orgId)), [dispatch])
}

export const useUpdateReportsTimeZone = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((timeZone?: TimeZone) => dispatch(updateReportsTimeZone(timeZone)), [dispatch])
}

export const useUpdateReportsNodeIds = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((nodeIds: string[]) => dispatch(updateReportsNodeIds(nodeIds)), [dispatch])
}

export const useUpdateReportsCommissioningStates = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (commissioningStates: ReportCommissioningStateFilter[]) =>
      dispatch(updateReportsCommissioningStates(commissioningStates)),
    [dispatch]
  )
}

export const useUpdateReportsFleetId = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((fleetId?: string) => dispatch(updateReportsFleetId(fleetId)), [dispatch])
}

export const useUpdateReportsDuration = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((duration: Duration) => dispatch(updateReportsDuration(duration)), [dispatch])
}

export const useUpdateReportsCustomTimeRange = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (customTimeRange?: StoredCustomTimeRange) => dispatch(updateReportsCustomTimeRange(customTimeRange)),
    [dispatch]
  )
}

export const useEnsureReportFilterOrgState = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(() => dispatch(ensureReportFilterOrgState()), [dispatch])
}

export const useSetReportsCurrentUser = dispatcherFactory(setCurrentUser)

/** Returns the selected energy org ID or the current org ID if no org is selected */
export function useSelectedReportsEnergyOrgIdOrCurrent() {
  const selectedOrgId = useSelectedReportsEnergyOrgId()
  const currentOrgId = useCurrentOrgId()
  return selectedOrgId ?? currentOrgId
}

/** Returns the selected vehicle org ID or the current org ID if no org is selected */
export function useSelectedReportsVehicleOrgIdOrCurrent() {
  const selectedOrgId = useSelectedReportsVehicleOrgId()
  const currentOrgId = useCurrentOrgId()
  return selectedOrgId ?? currentOrgId
}

/** Returns the saved report timezone or the user's preferred timezone. */
export function useSelectedReportsTimeZoneOrPreferred() {
  const selectedTimeZone = useSelectedReportsTimeZone()
  const { preferredTimeZone } = useUserPrefs()
  return selectedTimeZone ?? preferredTimeZone
}

// =============================================================================
// Customizable-report layout selectors and dispatch hooks
// =============================================================================

export const useCurrentReportsUserId = selectorHookFactory((state) => state.reports.currentUser)

export const useUserReportLayoutsState = selectorHookFactory((state) => {
  const userId = state.reports.currentUser
  if (!userId) return undefined
  return state.reports.reportLayouts?.[userId]
})

const EMPTY_SAVED_REPORT_ORDER: string[] = []

export const useSavedReportOrder = selectorHookFactory((state) => {
  const userId = state.reports.currentUser
  if (!userId) return EMPTY_SAVED_REPORT_ORDER
  return state.reports.savedReportOrder?.[userId] ?? EMPTY_SAVED_REPORT_ORDER
})

export const useSeedReportLayout = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layout: DashboardLayout) => dispatch(_seedReportLayout({ userId, layout })),
    [dispatch]
  )
}

export const useDispatchMoveOrSwapReportWidget = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, fromSlotId: string, toSlotId: string) =>
      dispatch(_moveOrSwapReportWidget({ userId, layoutId, fromSlotId, toSlotId })),
    [dispatch]
  )
}

export const useDispatchRemoveReportWidgetFromSlot = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, slotId: string) =>
      dispatch(_removeReportWidgetFromSlot({ userId, layoutId, slotId })),
    [dispatch]
  )
}

export const useDispatchSetReportWidgetInSlot = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, slotId: string, widget: WidgetInstance) =>
      dispatch(_setReportWidgetInSlot({ userId, layoutId, slotId, widget })),
    [dispatch]
  )
}

export const useDispatchApplyReportTemplate = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, template: DashboardLayout) => dispatch(_applyReportTemplate({ userId, template })),
    [dispatch]
  )
}

export const useDispatchAddReport = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((userId: string, report: DashboardLayout) => dispatch(_addReport({ userId, report })), [dispatch])
}

export const useDispatchCopyReportFilters = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, fromReportId: string, toReportId: string) =>
      dispatch((dispatch, getState) => {
        dispatch(
          _copyReportFilters({ userId, fromReportId, toReportId, ...getReportFilterOrgContext(getState()) })
        )
      }),
    [dispatch]
  )
}

export const useDispatchRenameReport = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, reportId: string, name: string) => dispatch(_renameReport({ userId, reportId, name })),
    [dispatch]
  )
}

export const useDispatchDeleteReport = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, reportId: string) => dispatch(_deleteReport({ userId, reportId })),
    [dispatch]
  )
}

export const useDispatchSetActiveReportId = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, reportId: string) => dispatch(_setActiveReportId({ userId, reportId })),
    [dispatch]
  )
}

export const useDispatchSetActiveStandardReport = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, templateId: string) => dispatch(_setActiveStandardReport({ userId, templateId })),
    [dispatch]
  )
}

export const useDispatchSetActiveLegacyReportPage = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, page: LegacyReportPage) =>
      dispatch((dispatch, getState) => {
        dispatch(_setActiveLegacyReportPage({ userId, page, ...getReportFilterOrgContext(getState()) }))
      }),
    [dispatch]
  )
}

export const useDispatchReorderSavedReports = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, fromIndex: number, toIndex: number) =>
      dispatch(_reorderSavedReports({ userId, fromIndex, toIndex })),
    [dispatch]
  )
}

export const useDispatchChangeReportRowLayout = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (
      userId: string,
      layoutId: string,
      rowId: string,
      nextLayout: RowLayout,
      newSlotIds: string[],
      minSizeByWidgetId: Record<string, SlotSize | undefined>
    ) => dispatch(_changeReportRowLayout({ userId, layoutId, rowId, nextLayout, newSlotIds, minSizeByWidgetId })),
    [dispatch]
  )
}

export const useDispatchAddReportRow = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (
      userId: string,
      layoutId: string,
      afterRowId: string | null,
      newRowLayout: RowLayout,
      newRowId: string,
      newSlotIds: string[]
    ) => dispatch(_addReportRow({ userId, layoutId, afterRowId, newRowLayout, newRowId, newSlotIds })),
    [dispatch]
  )
}

export const useDispatchRemoveReportRow = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, rowId: string) => dispatch(_removeReportRow({ userId, layoutId, rowId })),
    [dispatch]
  )
}

export const useDispatchSetReportWidgetSettings = () => {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(
    (userId: string, layoutId: string, slotId: string, settings: unknown) =>
      dispatch(_setReportWidgetSettings({ userId, layoutId, slotId, settings })),
    [dispatch]
  )
}
